import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Mail, Download, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OrderConfirmation = () => {
  const [orderDetails, setOrderDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get order details from localStorage
    const lastOrder = localStorage.getItem('lastOrder');
    if (lastOrder) {
      setOrderDetails(JSON.parse(lastOrder));
    } else {
      // If no order found, redirect to home
      navigate('/');
    }
  }, [navigate]);

  if (!orderDetails) {
    return null;
  }

  const orderDate = new Date(orderDetails.orderDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="bg-white pt-16 min-h-screen">
      {/* Success Hero */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="inline-block mb-6"
          >
            <div className="bg-green-500 rounded-full p-6">
              <CheckCircle size={64} className="text-white" />
            </div>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Order Confirmed!
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-white/80 mb-8"
          >
            Thank you for your purchase. Your order has been successfully placed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 inline-block"
          >
            <p className="text-sm text-white/60 mb-2">Order Number</p>
            <p className="text-2xl font-bold font-mono">{orderDetails.orderId}</p>
          </motion.div>
        </div>
      </section>

      {/* Order Details */}
      <section className="py-16 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Customer Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white border border-grey rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold mb-6">Customer Information</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-mid-grey mb-1">Name</p>
                  <p className="font-semibold">{orderDetails.customerInfo.name}</p>
                </div>
                <div>
                  <p className="text-sm text-mid-grey mb-1">Email</p>
                  <p className="font-semibold">{orderDetails.customerInfo.email}</p>
                </div>
                {orderDetails.customerInfo.company && (
                  <div>
                    <p className="text-sm text-mid-grey mb-1">Company</p>
                    <p className="font-semibold">{orderDetails.customerInfo.company}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-mid-grey mb-1">Order Date</p>
                  <p className="font-semibold">{orderDate}</p>
                </div>
              </div>
            </motion.div>

            {/* Next Steps */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-br from-light-pink/10 to-mid-purple/10 border border-light-pink/20 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold mb-6">What's Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="text-light-pink mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold mb-1">Confirmation Email</p>
                    <p className="text-sm text-dark-grey">
                      We've sent a confirmation email to {orderDetails.customerInfo.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold mb-1">Project Kickoff</p>
                    <p className="text-sm text-dark-grey">
                      Our team will contact you within 24 hours to discuss your project
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Download className="text-mid-purple mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold mb-1">Documentation</p>
                    <p className="text-sm text-dark-grey">
                      Access your order details and project documentation anytime
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Order Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white border border-grey rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold mb-6">Order Items</h2>
            <div className="space-y-4">
              {orderDetails.items.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-start pb-4 border-b border-grey last:border-0"
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                    <p className="text-sm text-mid-grey uppercase tracking-wider mb-2">
                      {item.type}
                    </p>
                    {item.description && (
                      <p className="text-dark-grey text-sm">{item.description}</p>
                    )}
                    <p className="text-sm text-mid-grey mt-2">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xl font-bold bg-gradient-to-r from-light-pink to-mid-purple bg-clip-text text-transparent">
                      {item.price}
                    </p>
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t-2 border-dark-grey">
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold">Total</span>
                  <span className="text-3xl font-bold bg-gradient-to-r from-light-pink to-mid-purple bg-clip-text text-transparent">
                    ${orderDetails.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
          >
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-light-pink to-mid-purple text-white rounded-full font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
            >
              Back to Home
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-8 py-4 bg-white border-2 border-mid-purple text-mid-purple rounded-full font-semibold hover:bg-mid-purple hover:text-white transition-all"
            >
              Browse More Services
            </button>
          </motion.div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 px-4 lg:px-8 bg-light-grey">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help?</h2>
          <p className="text-lg text-dark-grey mb-6">
            Our support team is here to assist you with any questions.
          </p>
          <a
            href="mailto:info@syncorellc.com"
            className="inline-flex items-center gap-2 text-light-pink hover:text-mid-purple transition-colors font-semibold"
          >
            <Mail size={20} />
            info@syncorellc.com
          </a>
        </div>
      </section>
    </div>
  );
};

export default OrderConfirmation;
